import type {AfriexCurrencyCode} from '@afriex/core/domain/enums';

import {getChannelProcessors} from '@/constants/formatters';
import type {ProcessorCapabilityMatrix} from '@/services/processorService';
import type {AfriexCurrency, AfriexTransactionChannel} from '@/types';
import {
  AfriexTransactionProcessor,
  AfriexTransactionType,
  BUSINESS_RESTRICTED_PROCESSORS,
} from '@/types';
import type {
  CurrencySetting,
  CurrencyTransactionObject,
  ProcessorWithEmpty,
} from '@/types/Currency';

import type {ProcessorSettingTarget} from './types';

export const extractProcessorListFrom = (
  processorMap: CurrencyTransactionObject | undefined,
): {
  channel: AfriexTransactionChannel;
  currentProcessor: AfriexTransactionProcessor;
  processors: AfriexTransactionProcessor[];
}[] => {
  const processorList = [] as {
    channel: AfriexTransactionChannel;
    currentProcessor: AfriexTransactionProcessor;
    processors: AfriexTransactionProcessor[];
  }[];
  if (!processorMap) {
    return [];
  }
  Object.keys(processorMap).forEach((key: string) => {
    const entry = processorMap[key as AfriexTransactionChannel];
    if (key && entry?.currentProcessor) {
      processorList.push({
        channel: key as unknown as AfriexTransactionChannel,
        currentProcessor: entry.currentProcessor,
        processors: entry.processors ?? [],
      });
    }
  });
  return processorList;
};

export const validateProcessor = (processor: AfriexTransactionProcessor) => {
  const validProcessors = new Set(Object.values(AfriexTransactionProcessor));
  return validProcessors.has(processor);
};

/**
 * Strip retail-only rails out of a business or api subtree before it is sent.
 *
 * Both write paths PATCH the whole audience subtree, not just the slot being
 * edited, so a corridor configured with one of these before it was blocked
 * would make every later save fail validation. Dropping them here means the
 * first save of that target cleans it up instead.
 */
export const stripRestrictedProcessors = (
  subtree: CurrencySetting,
  target: ProcessorSettingTarget,
): CurrencySetting => {
  if (target === 'user') {
    return subtree;
  }
  const isRestricted = (name: unknown) =>
    BUSINESS_RESTRICTED_PROCESSORS.includes(name as AfriexTransactionProcessor);
  Object.values(subtree ?? {}).forEach((byChannel) => {
    Object.values((byChannel ?? {}) as Record<string, unknown>).forEach(
      (slot) => {
        if (!slot || typeof slot !== 'object') {
          return;
        }
        // Every channel value is one of these slots except `limits`, which
        // carries neither field and so falls through untouched. Cleared as ''
        // rather than deleted, which is what the endpoint reads as "no current
        // processor" (see `ProcessorWithEmpty` in types/Currency.ts).
        const entry = slot as {
          currentProcessor?: ProcessorWithEmpty;
          processors?: AfriexTransactionProcessor[];
        };
        if (isRestricted(entry.currentProcessor)) {
          entry.currentProcessor = '';
        }
        if (Array.isArray(entry.processors)) {
          entry.processors = entry.processors.filter(
            (name: unknown) => !isRestricted(name),
          );
        }
      },
    );
  });
  return subtree;
};

/**
 * Does this currency have any processor configured for one audience?
 *
 * The screen carries one row per currency document (64 of them), but only a
 * handful are wired for any given target, so this is what separates "a corridor
 * someone manages" from "a currency that merely exists". Deliberately derived
 * from the data rather than a list of interesting currencies, so a newly
 * configured corridor, crypto included, appears without a code change.
 */
export const hasConfiguredCorridor = (
  currency: AfriexCurrency | undefined,
  target: ProcessorSettingTarget,
): boolean => {
  const subtree = currency?.[target];
  if (!subtree) {
    return false;
  }
  return (
    extractProcessorListFrom(subtree.DEPOSIT).length > 0 ||
    extractProcessorListFrom(subtree.WITHDRAW).length > 0
  );
};

/** A processor configured on a corridor that is switched off in production. */
export type SwitchedOffInUse = {
  type: AfriexTransactionType;
  channel: AfriexTransactionChannel;
  processor: AfriexTransactionProcessor;
  status: 'dev' | 'retired';
  isCurrent: boolean;
};

/**
 * Processors configured on this currency's corridors (current or backup) that
 * the capability matrix marks as `dev` or `retired`, so routing will not use
 * them in production. Read from the matrix the server builds from the node
 * declarations, not from the admin flags.
 */
export const findSwitchedOffInUse = (
  currency: AfriexCurrency | undefined,
  target: ProcessorSettingTarget,
  capabilityMatrix: ProcessorCapabilityMatrix,
): SwitchedOffInUse[] => {
  const found: SwitchedOffInUse[] = [];
  const currencyCode = currency?.code as AfriexCurrencyCode;
  for (const type of [
    AfriexTransactionType.DEPOSIT,
    AfriexTransactionType.WITHDRAW,
  ]) {
    const configured = extractProcessorListFrom(currency?.[target]?.[type]);
    for (const status of ['dev', 'retired'] as const) {
      for (const {channel, processors: off} of getChannelProcessors(
        capabilityMatrix?.[status]?.[type],
        currencyCode,
        target,
      )) {
        const slot = configured.find((c) => c.channel === channel);
        if (slot && off.has(slot.currentProcessor)) {
          found.push({
            type,
            channel,
            processor: slot.currentProcessor,
            status,
            isCurrent: true,
          });
        }
        slot?.processors
          .filter((p) => p !== slot.currentProcessor && off.has(p))
          .forEach((processor) =>
            found.push({type, channel, processor, status, isCurrent: false}),
          );
      }
    }
  }
  return found;
};

export type CorridorFilter =
  'CONFIGURED' | 'CURRENT_OFF' | 'BACKUP_OFF' | 'NOT_CONFIGURED' | 'ALL';

/** Which filter buckets a currency row falls into for one audience. */
export const corridorFiltersOf = (
  currency: AfriexCurrency | undefined,
  target: ProcessorSettingTarget,
  capabilityMatrix: ProcessorCapabilityMatrix,
): CorridorFilter[] => {
  if (!hasConfiguredCorridor(currency, target)) {
    return ['ALL', 'NOT_CONFIGURED'];
  }
  const off = findSwitchedOffInUse(currency, target, capabilityMatrix);
  return [
    'ALL',
    'CONFIGURED',
    ...(off.some((o) => o.isCurrent) ? (['CURRENT_OFF'] as const) : []),
    ...(off.some((o) => !o.isCurrent) ? (['BACKUP_OFF'] as const) : []),
  ];
};
